import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { Redirect } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Card,
  CardContent,
  Chip,
  Checkbox,
  ListItemText,
  Divider,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import PageHeader from '../Common/PageHeader'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useAuthentication } from '../../hooks/useAuthentication'
import Loader from '../Loader'
import { GET_DEPARTMENTS, GET_FINANCIAL_YEARS } from '../Vouchers/gql'
import {
  GET_BUDGET_ACTUAL_BY_BRANCH_FOR_CATEGORIES,
  GET_BUDGET_VS_ACTUAL,
  GET_MY_BUDGET_ACCESS,
} from '../../api/budget'
import { findCurrentFinancialYear } from '../../utils/utils'

const formatSek = (amount: number) =>
  new Intl.NumberFormat('en-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(amount)

const JAMAT_CHART_COLORS = [
  '#0d6efd',
  '#20c997',
  '#fd7e14',
  '#6f42c1',
  '#d63384',
  '#198754',
]

const REMAINING_BUDGET_COLOR = '#ced4da'

const UNBUDGETED_OPTION = '__unbudgeted'

const categoryKey = (r: any) =>
  `${r.cost_category.id}:${r.sub_cost_category?.id ?? ''}`
const categoryLabel = (r: any) =>
  r.sub_cost_category?.name ?? r.cost_category.name

type Summary = {
  budgeted: number
  actual: number
  remaining: number
  unbudgetedActual: number
}

const summarize = (rows: any[]): Summary =>
  rows.reduce(
    (acc: Summary, r: any) => ({
      budgeted: acc.budgeted + r.budgeted,
      actual: acc.actual + r.actual,
      remaining: acc.remaining + r.remaining,
      unbudgetedActual: acc.unbudgetedActual + (r.budgeted > 0 ? 0 : r.actual),
    }),
    { budgeted: 0, actual: 0, remaining: 0, unbudgetedActual: 0 },
  )

const UnbudgetedChip = () => (
  <Chip size="small" color="warning" variant="outlined" label="Unbudgeted" />
)

const renderPercent = (percent: number | null, actual: number) => {
  if (percent !== null) return `${percent.toFixed(0)}%`
  return actual > 0 ? <UnbudgetedChip /> : '—'
}

const BudgetReport = () => {
  const { authState } = useAuthentication()

  const myAccessResult = useQuery(GET_MY_BUDGET_ACCESS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const accessLevel = myAccessResult.data?.myBudgetAccess?.level
  const ownDepartment = myAccessResult.data?.myBudgetAccess?.department

  const financialYearsResult = useQuery(GET_FINANCIAL_YEARS, {
    context: { headers: { authorization: authState.authToken } },
  })
  const departmentsResult = useQuery(GET_DEPARTMENTS, {
    context: { headers: { authorization: authState.authToken } },
  })

  const departmentOptions = useMemo(() => {
    const all = departmentsResult.data?.departments ?? []
    if (accessLevel === 'OWN' && ownDepartment) {
      return all.filter((d: any) => d.id === ownDepartment.id)
    }
    return all
  }, [departmentsResult.data, accessLevel, ownDepartment])

  const [selectedFinancialYear, setSelectedFinancialYear] = useState<string>('')
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])

  useEffect(() => {
    if (accessLevel === 'OWN' && ownDepartment) {
      setSelectedDepartment(ownDepartment.id)
    }
  }, [accessLevel, ownDepartment])

  useEffect(() => {
    if (selectedFinancialYear || !financialYearsResult.data?.financialYears)
      return
    const current = findCurrentFinancialYear(
      financialYearsResult.data.financialYears,
    )
    setSelectedFinancialYear(
      current
        ? String(current)
        : String(financialYearsResult.data.financialYears[0]?.id ?? ''),
    )
  }, [financialYearsResult.data, selectedFinancialYear])

  const budgetVsActualResult = useQuery(GET_BUDGET_VS_ACTUAL, {
    variables: { financial_year_id: selectedFinancialYear },
    skip: !selectedFinancialYear || accessLevel === 'NONE' || !accessLevel,
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'network-only',
  })

  const branchResult = useQuery(GET_BUDGET_ACTUAL_BY_BRANCH_FOR_CATEGORIES, {
    variables: {
      financial_year_id: selectedFinancialYear,
      department_id: selectedDepartment,
    },
    skip:
      !selectedFinancialYear ||
      selectedDepartment === 'All' ||
      accessLevel === 'NONE' ||
      !accessLevel,
    context: { headers: { authorization: authState.authToken } },
    fetchPolicy: 'network-only',
  })

  const branchChartDataByRow = useMemo(() => {
    const all = branchResult.data?.budgetActualByBranchForCategories ?? []
    const byRow = new Map<string, { name: string; value: number }[]>()
    for (const row of all) {
      if (row.actual <= 0) continue
      const key = `${row.cost_category_id}:${row.sub_cost_category_id ?? ''}`
      const existing = byRow.get(key) ?? []
      existing.push({ name: row.branch, value: row.actual })
      byRow.set(key, existing)
    }
    return byRow
  }, [branchResult.data])

  const categoryOptions = useMemo(() => {
    const all = budgetVsActualResult.data?.budgetVsActual ?? []
    const byKey = new Map<string, string>()
    for (const r of all) byKey.set(categoryKey(r), categoryLabel(r))
    return Array.from(byKey, ([key, label]) => ({ key, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    )
  }, [budgetVsActualResult.data])

  const unbudgetedOnly = selectedCategories.includes(UNBUDGETED_OPTION)
  const selectedCategoryKeys = useMemo(
    () => selectedCategories.filter((c) => c !== UNBUDGETED_OPTION),
    [selectedCategories],
  )

  const rows = useMemo(() => {
    const all = budgetVsActualResult.data?.budgetVsActual ?? []
    const filtered = all.filter(
      (r: any) =>
        (selectedDepartment === 'All' ||
          r.department.id === selectedDepartment) &&
        (selectedCategoryKeys.length === 0 ||
          selectedCategoryKeys.includes(categoryKey(r))) &&
        (!unbudgetedOnly || r.budgeted <= 0),
    )
    return [...filtered].sort((a: any, b: any) => {
      const deptCompare = a.department.name.localeCompare(b.department.name)
      return deptCompare !== 0
        ? deptCompare
        : categoryLabel(a).localeCompare(categoryLabel(b))
    })
  }, [
    budgetVsActualResult.data,
    selectedDepartment,
    selectedCategoryKeys,
    unbudgetedOnly,
  ])

  const departmentGroups = useMemo(() => {
    const groups: { department: any; rows: any[] }[] = []
    for (const r of rows) {
      const last = groups[groups.length - 1]
      if (last && last.department.id === r.department.id) {
        last.rows.push(r)
      } else {
        groups.push({ department: r.department, rows: [r] })
      }
    }
    return groups
  }, [rows])

  const totals = summarize(rows)
  const showDepartmentSubtotals = selectedDepartment === 'All'

  const renderSummaryRow = (
    key: string,
    label: string,
    summary: Summary,
    grand: boolean,
  ) => (
    <TableRow
      key={key}
      sx={{
        ...(grand ? {} : { bgcolor: 'grey.50' }),
        '& td': {
          fontWeight: grand ? 700 : 600,
          fontSize: grand ? '0.98rem' : '0.95rem',
          py: 1.5,
          verticalAlign: 'top',
        },
      }}
    >
      <TableCell colSpan={2}>{label}</TableCell>
      <TableCell align="right">{formatSek(summary.budgeted)}</TableCell>
      <TableCell align="right">
        {formatSek(summary.actual)}
        {summary.unbudgetedActual > 0 && (
          <Typography
            component="div"
            variant="caption"
            color="warning.dark"
            sx={{ fontWeight: 400 }}
          >
            incl. {formatSek(summary.unbudgetedActual)} unbudgeted
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">{formatSek(summary.remaining)}</TableCell>
      <TableCell align="right">
        {renderPercent(
          summary.budgeted > 0
            ? (summary.actual / summary.budgeted) * 100
            : null,
          summary.actual,
        )}
      </TableCell>
    </TableRow>
  )

  if (myAccessResult.loading) {
    return <Loader />
  }

  if (accessLevel === 'NONE' || !accessLevel) {
    return <Redirect to="/panel" />
  }

  if (
    financialYearsResult.loading ||
    departmentsResult.loading ||
    (!selectedFinancialYear &&
      !!financialYearsResult.data?.financialYears?.length)
  ) {
    return <Loader />
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Budget vs Actual" icon={AccountBalanceIcon} />
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ fontSize: '0.95rem', mb: 2 }}
      >
        Actual reflects vouchers that have reached at least the Approved state
        for the selected financial year. Note: figures for the most recent
        submissions may look understated if their vouchers are still working
        through approval.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3, alignItems: 'flex-end' }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Financial Year"
            value={selectedFinancialYear}
            onChange={(e) => setSelectedFinancialYear(e.target.value)}
          >
            {financialYearsResult.data?.financialYears?.map((fy: any) => (
              <MenuItem key={fy.id} value={fy.id}>
                {fy.year}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Department"
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            disabled={accessLevel === 'OWN'}
          >
            {accessLevel === 'ALL' && <MenuItem value="All">All</MenuItem>}
            {departmentOptions.map((dept: any) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Cost Categories"
            value={selectedCategories}
            onChange={(e) => {
              const value = e.target.value as unknown as string | string[]
              setSelectedCategories(
                typeof value === 'string' ? value.split(',') : value,
              )
            }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected: unknown) => {
                const keys = selected as string[]
                if (keys.length === 0) return 'All'
                const labels = categoryOptions
                  .filter((o) => keys.includes(o.key))
                  .map((o) => o.label)
                if (keys.includes(UNBUDGETED_OPTION)) {
                  labels.unshift('Unbudgeted')
                }
                return labels.join(', ')
              },
            }}
          >
            <MenuItem value={UNBUDGETED_OPTION}>
              <Checkbox size="small" checked={unbudgetedOnly} />
              <ListItemText primary="Unbudgeted only" />
            </MenuItem>
            <Divider />
            {categoryOptions.map((o) => (
              <MenuItem key={o.key} value={o.key}>
                <Checkbox
                  size="small"
                  checked={selectedCategoryKeys.includes(o.key)}
                />
                <ListItemText primary={o.label} />
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {budgetVsActualResult.loading ? (
        <Loader />
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    bgcolor: 'grey.100',
                    py: 1.5,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    color: 'text.secondary',
                  },
                }}
              >
                <TableCell>Department</TableCell>
                <TableCell>Cost Category</TableCell>
                <TableCell align="right">Budgeted</TableCell>
                <TableCell align="right">Actual</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="right">% Used</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {departmentGroups.map((group) => (
                <React.Fragment key={group.department.id}>
                  {group.rows.map((r: any) => (
                    <TableRow
                      key={`${r.department.id}:${r.cost_category.id}:${r.sub_cost_category?.id ?? ''}`}
                      sx={{
                        ...(r.remaining < 0
                          ? {
                              bgcolor: (theme) =>
                                alpha(theme.palette.error.main, 0.08),
                            }
                          : {}),
                        '& td': { fontSize: '0.95rem', py: 1.5 },
                      }}
                    >
                      <TableCell>{r.department.name}</TableCell>
                      <TableCell>
                        {r.sub_cost_category?.name ?? r.cost_category.name}
                      </TableCell>
                      <TableCell align="right">
                        {formatSek(r.budgeted)}
                      </TableCell>
                      <TableCell align="right">{formatSek(r.actual)}</TableCell>
                      <TableCell align="right">
                        {formatSek(r.remaining)}
                      </TableCell>
                      <TableCell align="right">
                        {renderPercent(r.percent_used, r.actual)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {showDepartmentSubtotals &&
                    renderSummaryRow(
                      `subtotal:${group.department.id}`,
                      `${group.department.name} subtotal`,
                      summarize(group.rows),
                      false,
                    )}
                </React.Fragment>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ color: 'text.secondary', fontSize: '0.95rem', py: 2 }}
                  >
                    {unbudgetedOnly
                      ? 'No unbudgeted spend for this selection.'
                      : 'No budgets set for this selection yet.'}
                  </TableCell>
                </TableRow>
              )}
              {rows.length > 0 &&
                renderSummaryRow('total', 'Total', totals, true)}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selectedDepartment !== 'All' && !budgetVsActualResult.loading && (
        <>
          <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
            Actual Spend by Branch
          </Typography>
          {branchResult.loading ? (
            <Loader />
          ) : (
            <Grid container spacing={2}>
              {rows
                .filter((r: any) => r.actual > 0)
                .map((r: any) => {
                  const key = categoryKey(r)
                  const branchSlices = branchChartDataByRow.get(key) ?? []
                  const label = categoryLabel(r)
                  const hasBudget = r.budgeted > 0
                  const overBudget = hasBudget && r.remaining < 0
                  const chartData =
                    hasBudget && !overBudget
                      ? [
                          ...branchSlices,
                          {
                            name: 'Remaining',
                            value: r.remaining,
                            isRemaining: true,
                          },
                        ]
                      : branchSlices
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            }}
                          >
                            <Typography variant="subtitle1" component="h3">
                              {label}
                            </Typography>
                            {overBudget && (
                              <Chip
                                size="small"
                                color="error"
                                label={`Over by ${formatSek(Math.abs(r.remaining))}`}
                              />
                            )}
                          </Box>
                          {chartData.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No branch data.
                            </Typography>
                          ) : (
                            <ResponsiveContainer width="100%" height={220}>
                              <PieChart>
                                <Pie
                                  data={chartData}
                                  dataKey="value"
                                  nameKey="name"
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={70}
                                  minAngle={4}
                                  label={({ percent }: any) =>
                                    `${(percent * 100).toFixed(0)}%`
                                  }
                                >
                                  {chartData.map(
                                    (entry: any, index: number) => (
                                      <Cell
                                        key={index}
                                        fill={
                                          entry.isRemaining
                                            ? REMAINING_BUDGET_COLOR
                                            : JAMAT_CHART_COLORS[
                                                index %
                                                  JAMAT_CHART_COLORS.length
                                              ]
                                        }
                                      />
                                    ),
                                  )}
                                </Pie>
                                <Tooltip
                                  formatter={(value: number) =>
                                    formatSek(value)
                                  }
                                />
                                <Legend />
                              </PieChart>
                            </ResponsiveContainer>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                })}
              {rows.filter((r: any) => r.actual > 0).length === 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    No spend recorded yet for this selection.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </>
      )}
    </Box>
  )
}

export default BudgetReport
